import { useDeleteFileMutation } from 'store/services/documentStorage/googleCloudStorage';
import { type FC, type KeyboardEvent, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { type FileIconProps, FileIcon as OriginalFileIcon, defaultStyles } from 'react-file-icon';
import { ContentCopy, Delete, Download, Launch, MoreVert } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { ReactNativeMessageType, type FileResponse } from 'types';
import { invokeRNMessage, isMobileApp } from 'utils/common';
import { STORE_KEYS } from 'types/store';
import { useStore } from 'contexts';
import { Box, CircularProgress, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { DesktopActionButton, DesktopActions, FileWrapper, Image, ImageContainer, TextContainer } from './FileItem.styles';
import { getBaseUrl } from 'utils/baseUrl';
import { PreviewItem } from './PreviewItem';
import { getFileIconExtension } from 'utils/file/fileUtils';
import { isKnownIconExtension } from 'utils/file/fileIconUtils';

export const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'bmp', 'svg', 'webp', 'avif'];
const UNSUPPORTED_PREVIEW_EXTENSIONS = ['ppt', 'pptx', 'rtf', 'doc', 'odt', 'ods', 'xls', 'csv'];

interface Props {
  file: FileResponse;
  disabledRemove?: boolean;
  /** When true, remove only updates UI/form; does not call storage delete API. */
  skipStorageDelete?: boolean;
  onChange?: () => void;
  onRemove?: () => void;
  onOpenPreview?: () => void;
}

const FileIcon = OriginalFileIcon as FC<FileIconProps>;

const FileItemBase = ({ file, disabledRemove, skipStorageDelete, onChange, onRemove, onOpenPreview }: Props) => {
  const { t } = useTranslation();
  const mobileApp = isMobileApp();
  const { getItem } = useStore();
  const auth = getItem(STORE_KEYS.AUTH);
  const [deleteFile] = useDeleteFileMutation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [imageState, setImageState] = useState<{ src: string | null; loading: boolean }>({ src: null, loading: false });

  const [previewState, setPreviewState] = useState<{ open: boolean; url: Blob | string | null; loading: boolean }>({
    open: false,
    url: null,
    loading: false,
  });

  const isGoogleDriveFile = file.typeOfStorage === 'Gdrive';

  const iconExtension = useMemo(
    () => getFileIconExtension(isGoogleDriveFile ? undefined : file.fileType, file.name),
    [isGoogleDriveFile, file.fileType, file.name]
  );

  const iconStyles = useMemo(() => (isKnownIconExtension(iconExtension) ? defaultStyles[iconExtension] : {}), [iconExtension]);

  const isPreviewSupported = useMemo(() => {
    if (mobileApp) return true;
    if (isGoogleDriveFile) return true;
    return !UNSUPPORTED_PREVIEW_EXTENSIONS.includes(iconExtension);
  }, [mobileApp, isGoogleDriveFile, iconExtension]);

  useEffect(() => {
    const isImage = IMAGE_EXTENSIONS.includes(iconExtension);
    if (!auth?.jwtToken || !file.url || !isImage) return;
    let objectUrl: string | null = null;
    setImageState((prev) => ({ ...prev, loading: true }));

    const fetchImage = async () => {
      try {
        const response = await fetch(file.url, {
          headers: { Authorization: auth.jwtToken ?? '' },
        });

        if (!response.ok) throw new Error('Failed to fetch image');
        const blob = await response.blob();

        objectUrl = URL.createObjectURL(blob);
        setImageState({ src: objectUrl, loading: false });
      } catch {
        setImageState({ src: null, loading: false });
      }
    };

    fetchImage();

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [iconExtension, file.url, auth?.jwtToken]);

  const handleOpen = useCallback(async () => {
    onOpenPreview?.();

    if (!isPreviewSupported) return;
    if (isGoogleDriveFile) {
      if (mobileApp) invokeRNMessage(ReactNativeMessageType.OpenGoogleDriveFile, { file });
      else {
        setPreviewState({ open: true, url: file.url, loading: false });
      }
      return;
    }
    if (mobileApp) {
      invokeRNMessage(ReactNativeMessageType.OpenFile, { file, token: auth?.jwtToken ?? '' });
      return;
    } else {
      if (!file.url) {
        toast.error(t('user.detail.file.loadFailed'));
        return;
      }
      try {
        setPreviewState((prev) => ({ ...prev, loading: true }));
        const response = await fetch(file.url, { headers: { Authorization: auth?.jwtToken ?? '' } });
        if (!response.ok) throw new Error('Failed to load file');

        const blob = await response.blob();

        setPreviewState({ open: true, url: blob, loading: false });
      } catch {
        setPreviewState((prev) => ({ ...prev, loading: false }));
        toast.error(t('user.detail.file.loadFailed'));
      }
    }
  }, [isPreviewSupported, isGoogleDriveFile, mobileApp, file, auth?.jwtToken, onOpenPreview, t]);

  const handleFileKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) return;
      event.preventDefault();
      void handleOpen();
    },
    [handleOpen]
  );

  const handleDownload = useCallback(async () => {
    if (mobileApp) {
      invokeRNMessage(ReactNativeMessageType.DownloadFile, { file, token: auth?.jwtToken ?? '' });
      return;
    }

    if (isGoogleDriveFile) {
      window.open(file.url, '_blank');
      return;
    }

    if (!file.url) {
      toast.error(t('user.detail.file.loadFailed'));
      return;
    }

    try {
      const response = await fetch(file.url, { headers: { Authorization: auth?.jwtToken ?? '' } });
      if (!response.ok) throw new Error('Failed to download');

      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch {
      toast.error(t('user.detail.file.loadFailed'));
    }
  }, [file, auth?.jwtToken, mobileApp, isGoogleDriveFile, t]);

  const handleRemove = useCallback(async () => {
    if (disabledRemove) return;
    if (!skipStorageDelete && !isGoogleDriveFile) {
      await deleteFile({ id: file.id });
    }
    onRemove?.();
    onChange?.();
  }, [disabledRemove, skipStorageDelete, isGoogleDriveFile, onRemove, onChange, deleteFile, file.id]);

  const handleClosePreview = useCallback(() => setPreviewState({ open: false, url: null, loading: false }), []);

  const handleCopy = useCallback(async () => {
    if (!file.url) {
      toast.error(t('user.detail.file.loadFailed'));
      return;
    }
    const url = getBaseUrl(`/user/download/${encodeURIComponent(file.url)}?fileName=${encodeURIComponent(file.name)}`);
    await navigator.clipboard.writeText(url);
    toast.success(t('common.copied'));
  }, [file, t]);

  const menuItems = [
    { icon: <Delete sx={{ mr: 1 }} />, label: 'common.delete', action: handleRemove, show: !disabledRemove },
    {
      icon: <Download sx={{ mr: 1 }} />,
      label: 'common.download',
      action: handleDownload,
      show: !isGoogleDriveFile,
      disabled: !file.url,
    },
    {
      icon: <Launch sx={{ mr: 1 }} />,
      label: 'common.view',
      action: handleOpen,
      show: !mobileApp,
      disabled: !isPreviewSupported || (!isGoogleDriveFile && !file.url),
      tooltip: !isPreviewSupported ? t('user.detail.file.preview.notSupported') : undefined,
    },
    {
      icon: <ContentCopy sx={{ mr: 1 }} />,
      label: 'user.detail.file.copyLink',
      action: handleCopy,
      show: !isGoogleDriveFile,
      disabled: !file.url,
    },
  ];

  return (
    <FileWrapper
      elevation={1}
      tabIndex={0}
      role="group"
      aria-label={file.name}
      onKeyDown={handleFileKeyDown}
      onClick={mobileApp ? handleOpen : undefined}
      onDoubleClick={mobileApp ? undefined : handleOpen}>
      <ImageContainer>
        {previewState.loading ? (
          <CircularProgress size={32} />
        ) : IMAGE_EXTENSIONS.includes(iconExtension) ? (
          imageState.loading ? (
            <CircularProgress size={32} />
          ) : imageState.src ? (
            <Image src={imageState.src} alt={file?.name ?? 'file_img'} />
          ) : (
            <Box width="48px" height="48px">
              <FileIcon extension={iconExtension} {...iconStyles} />
            </Box>
          )
        ) : (
          <Box width="48px" height="48px">
            <FileIcon extension={iconExtension} {...iconStyles} />
          </Box>
        )}
      </ImageContainer>

      <TextContainer>
        <Box overflow="hidden">
          <Tooltip title={file.name} placement="top">
            <Typography noWrap variant="body2" fontWeight="500" sx={{ userSelect: 'none', WebkitUserSelect: 'none' }}>
              {file.name}
            </Typography>
          </Tooltip>
          {file.fileSize ? <Typography variant="body2">{`${file.fileSize} MB`}</Typography> : <div />}
        </Box>

        {mobileApp && (
          <IconButton
            onClick={(event) => {
              event.stopPropagation();
              setAnchorEl(event.currentTarget);
            }}
            size="small">
            <MoreVert />
          </IconButton>
        )}
      </TextContainer>

      {!mobileApp && (
        <DesktopActions className="file-item-desktop-actions">
          {menuItems.map(
            ({ icon, label, action, show, tooltip, disabled }) =>
              show && (
                <Tooltip key={label} title={tooltip || ''} placement="left" disableHoverListener={!disabled}>
                  <span>
                    <DesktopActionButton
                      fullWidth
                      color="inherit"
                      disabled={disabled}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (!disabled) action();
                      }}
                      size="small">
                      {icon}
                      <Typography variant="body2">{t(label)}</Typography>
                    </DesktopActionButton>
                  </span>
                </Tooltip>
              )
          )}
        </DesktopActions>
      )}

      {mobileApp && (
        <Menu
          disableScrollLock
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClick={(event) => event.stopPropagation()}
          onClose={() => setAnchorEl(null)}>
          {menuItems.map(
            ({ icon, label, action, show, tooltip, disabled }) =>
              show && (
                <Tooltip key={label} title={tooltip || ''} placement="left" disableHoverListener={!disabled}>
                  <span>
                    <MenuItem
                      disabled={disabled}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (!disabled) {
                          action();
                          setAnchorEl(null);
                        }
                      }}>
                      {icon}
                      <Typography variant="body2">{t(label)}</Typography>
                    </MenuItem>
                  </span>
                </Tooltip>
              )
          )}
        </Menu>
      )}

      <PreviewItem
        open={previewState.open}
        onClose={handleClosePreview}
        url={previewState.url ?? ''}
        name={file.name}
        typeOfStorage={file.typeOfStorage}
        fileType={file.fileType}
      />
    </FileWrapper>
  );
};

export const FileItem = memo(FileItemBase) as typeof FileItemBase;
